"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import { useOptionalTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import { useCreateSearch } from "@/hooks/use-create-search";
import { usePendingNqlMessage } from "@/stores/use-pending-nql-message";

/**
 * Kicks off a "find profiles similar to" agent search from a button click.
 *
 * Bundles three side-effects so callers stay declarative:
 *   1. POST /projects/{id}/searches with similar_profile_ids
 *   2. Stash the server-composed seed message in usePendingNqlMessage
 *   3. router.push to /talent-search/<new search id>
 *
 * The destination page's chat panel consumes pending and POSTs to the
 * existing /assistant/{id}/chat endpoint — see assistant-provider.tsx:86-98.
 *
 * Note: `start` re-creates on every render because `createSearch` from
 * useCreateSearch is not useCallback-wrapped. This is accepted and harmless —
 * each render's `start` always calls the latest `createSearch` reference.
 *
 * @param projectId Project to attach the new search to. From the profile
 *   sheet that's `useTalentSearch().currentProjectId`; from the pipeline
 *   bulk-actions it's `usePipelineJob().job.project_id`. Pass null when
 *   neither is available and the hook will no-op.
 */
export function useStartSimilarSearch(projectId: string | null) {
  const router = useRouter();
  const { createSearch, isPending } = useCreateSearch({ projectId });
  // Inside a search session (or the hero), search the same pools; elsewhere
  // (job pipeline) the org's default-search pools.
  const poolIds = useOptionalTalentSearchFilters()?.poolIds;
  const pool_ids = poolIds?.length ? poolIds : undefined;

  const start = useCallback(
    async (profileIds: string[]) => {
      if (profileIds.length < 1 || profileIds.length > 10) return;
      if (!projectId) return;

      const data = await createSearch({ similar_profile_ids: profileIds, pool_ids });

      if (data.seeded_message) {
        usePendingNqlMessage.getState().set({
          kind: "similar_profile_ids",
          searchId: data.search.id,
          message: data.seeded_message,
        });
      }
      router.push(`/talent-search/${data.search.id}`);
    },
    [createSearch, pool_ids, projectId, router],
  );

  return { start, isPending };
}
